import { fetchAsociacionByPhone } from './association';
import { ATENCION_BOT_BASE_URL } from '../config/atencionBot';
import { API_BASE_URL } from '../config/env';
import {
  formatWhatsappPhone,
  getClientDisplayNameFromProfile,
  normalizeDigits
} from '../utils/chatwootContactDisplay';

async function parseJson(res) {
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(data?.message || `Error ${res.status}`);
  }
  return data;
}

function extractAtencionPedidoId(value) {
  if (Number.isFinite(Number(value)) && Number(value) > 0) {
    return Number(value);
  }

  const raw = String(value || '').trim();
  if (!raw) return NaN;

  const nroMatch = raw.match(/^AT-(?:atencion-)?(?:atencion-)?(\d+)$/i);
  if (nroMatch) return Number(nroMatch[1]);

  const cardMatch = raw.match(/^(?:atencion-)+(\d+)$/i);
  if (cardMatch) return Number(cardMatch[1]);

  const tailMatch = raw.match(/(\d+)$/);
  if (tailMatch) return Number(tailMatch[1]);

  return NaN;
}

export function formatAtencionPedidoNro(rawValue, fallbackId = null) {
  const raw = String(rawValue || '').trim();
  const resolvedId = extractAtencionPedidoId(raw) || extractAtencionPedidoId(fallbackId);
  if (!raw) return Number.isFinite(resolvedId) && resolvedId > 0 ? `AT-${resolvedId}` : '';
  if (/^AT-\d+$/i.test(raw)) return `AT-${extractAtencionPedidoId(raw)}`;
  if (Number.isFinite(resolvedId) && resolvedId > 0) return `AT-${resolvedId}`;
  return raw;
}

export async function fetchAtencionPedido(pedidoId) {
  const id = extractAtencionPedidoId(pedidoId);
  if (!Number.isFinite(id) || id <= 0) {
    throw new Error('ID de pedido inválido');
  }
  const res = await fetch(`${API_BASE_URL}/api/atencion-bot/pedidos/${id}`, {
    credentials: 'include'
  });
  const data = await parseJson(res);
  return data?.pedido || null;
}

export async function fetchAtencionConversaciones({ limit = 50, localOnly = true } = {}) {
  const params = new URLSearchParams();
  params.set('limit', String(limit));
  params.set('local_only', localOnly ? 'true' : 'false');
  const res = await fetch(`${API_BASE_URL}/api/atencion-bot/conversaciones?${params}`, {
    credentials: 'include'
  });
  const data = await parseJson(res);
  return Array.isArray(data?.conversaciones) ? data.conversaciones : [];
}

export async function fetchAtencionConversacion(conversationId) {
  const convId = String(conversationId || '').trim();
  if (!convId) throw new Error('conversation_id requerido');
  const res = await fetch(
    `${API_BASE_URL}/api/atencion-bot/conversaciones/${encodeURIComponent(convId)}`,
    { credentials: 'include' }
  );
  return parseJson(res);
}

export async function fetchAtencionPedidos({ conversationId, limit = 20 } = {}) {
  const params = new URLSearchParams();
  if (conversationId) params.set('conversation_id', conversationId);
  if (limit) params.set('limit', String(limit));
  const qs = params.toString();
  const res = await fetch(`${API_BASE_URL}/api/atencion-bot/pedidos${qs ? `?${qs}` : ''}`, {
    credentials: 'include'
  });
  const data = await parseJson(res);
  return Array.isArray(data?.pedidos) ? data.pedidos : [];
}

export async function fetchAtencionPedidosFarmacia({ limit = 100, metodoEntrega, sucursalCodigo } = {}) {
  const params = new URLSearchParams();
  if (limit) params.set('limit', String(limit));
  const metodoEntregaKey = String(metodoEntrega || '').trim().toLowerCase();
  if (metodoEntregaKey === 'mostrador' || metodoEntregaKey === 'domicilio') {
    params.set('metodo_entrega', metodoEntregaKey);
  }
  const sucursalCodigoKey = String(sucursalCodigo || '').trim();
  if (sucursalCodigoKey) params.set('sucursal_codigo', sucursalCodigoKey);
  const res = await fetch(`${API_BASE_URL}/api/atencion-bot/pedidos-farmacia?${params}`, {
    credentials: 'include'
  });
  const data = await parseJson(res);
  return Array.isArray(data?.pedidos) ? data.pedidos : [];
}

export async function fetchAtencionSucursalesOperativas() {
  const res = await fetch(`${API_BASE_URL}/api/atencion-bot/sucursales-operativas`, {
    credentials: 'include'
  });
  const data = await parseJson(res);
  return Array.isArray(data?.sucursales) ? data.sucursales : [];
}

export async function updateAtencionPedidoSucursal(pedidoId, sucursalCodigo) {
  const id = extractAtencionPedidoId(pedidoId);
  if (!Number.isFinite(id) || id <= 0) throw new Error('ID de pedido inválido');
  const body = JSON.stringify({ sucursal_codigo: String(sucursalCodigo || '').trim() });
  try {
    const res = await fetch(`${API_BASE_URL}/api/atencion-bot/pedidos/${id}/sucursal`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body
    });
    return await parseJson(res);
  } catch (error) {
    if (!ATENCION_BOT_BASE_URL) throw error;
    const res = await fetch(`${ATENCION_BOT_BASE_URL}/admin/pedidos/${id}/sucursal`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body
    });
    if (!res.ok) throw error;
    return res.json().catch(() => ({}));
  }
}

export function isAtencionFarmaciaCardShape(pedido) {
  return (
    (pedido?.origen === 'bot_mostrador' || pedido?.origen === 'bot_domicilio') &&
    (pedido?.atencion_pedido_id || String(pedido?.id || '').startsWith('atencion-'))
  );
}

function looksLikePhoneLabel(value) {
  const raw = String(value || '').trim();
  if (!raw) return false;
  const digits = normalizeDigits(raw);
  if (digits.length < 8) return false;
  return /^[+()\d\s-]+$/.test(raw);
}

export function normalizeAtencionFarmaciaCard(card) {
  let base = { ...(card || {}) };
  if (!isAtencionFarmaciaCardShape(base)) {
    if (Array.isArray(base?.items) || base?.conversation_id) {
      base = mapAtencionPedidoToFarmaciaCard(base);
    } else {
      return base;
    }
  }

  let phoneRaw = String(base.phone || '').trim();
  const pacienteRaw = String(base.paciente || '').trim();
  if (!phoneRaw && looksLikePhoneLabel(pacienteRaw)) {
    phoneRaw = pacienteRaw;
  }

  const telefono =
    String(base.telefono || '').trim() || formatAtencionPedidoPhone(phoneRaw);
  let clienteNombre = String(base.cliente_nombre || '').trim();
  if (!clienteNombre && pacienteRaw && !looksLikePhoneLabel(pacienteRaw)) {
    clienteNombre = pacienteRaw;
  }

  return {
    ...base,
    phone: phoneRaw || base.phone || null,
    telefono,
    cliente_nombre: clienteNombre || null,
    paciente: clienteNombre || 'Cliente mostrador'
  };
}

export function getAtencionFarmaciaContactLines(card) {
  const normalized = normalizeAtencionFarmaciaCard(card);
  const nombre =
    normalized.cliente_nombre ||
    (normalized.paciente && normalized.paciente !== 'Cliente mostrador'
      ? normalized.paciente
      : '') ||
    'Cliente mostrador';
  return {
    nombre,
    telefono: normalized.telefono || ''
  };
}

export function ensureAtencionFarmaciaCard(pedido) {
  return normalizeAtencionFarmaciaCard(
    isAtencionFarmaciaCardShape(pedido) ? pedido : mapAtencionPedidoToFarmaciaCard(pedido)
  );
}

export function formatAtencionPedidoPhone(phone) {
  const formatted = formatWhatsappPhone(phone);
  if (formatted) return formatted;
  const digits = normalizeDigits(phone);
  if (digits && !/^0+$/.test(digits) && digits.length === 10) {
    return `+54 9 ${digits.slice(0, 3)} ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  return '';
}

const atencionNameByPhoneCache = new Map();

async function fetchBotClienteNombre(phone) {
  if (!ATENCION_BOT_BASE_URL) return '';
  const raw = String(phone || '').trim();
  if (!raw) return '';
  try {
    const params = new URLSearchParams({ phone: raw });
    const res = await fetch(`${ATENCION_BOT_BASE_URL}/admin/cliente-by-phone?${params}`);
    if (!res.ok) return '';
    const json = await res.json().catch(() => ({}));
    return String(json?.nombre || '').trim();
  } catch {
    return '';
  }
}

async function resolveAtencionClienteNombre(phone) {
  const digits = normalizeDigits(phone);
  if (!digits || /^0+$/.test(digits)) return '';
  if (atencionNameByPhoneCache.has(digits)) {
    return atencionNameByPhoneCache.get(digits);
  }
  let name = await fetchBotClienteNombre(phone);
  if (!name) {
    try {
      const profile = await fetchAsociacionByPhone(phone);
      name = getClientDisplayNameFromProfile(profile) || '';
    } catch {
      name = '';
    }
  }
  if (name) {
    atencionNameByPhoneCache.set(digits, name);
  }
  return name;
}

export async function enrichAtencionFarmaciaCardsWithNames(cards = []) {
  const list = Array.isArray(cards) ? cards : [];
  return Promise.all(
    list.map(async (card) => {
      const normalized = normalizeAtencionFarmaciaCard(card);
      if (!isAtencionFarmaciaCardShape(normalized)) return card;
      const telefono = normalized.telefono || formatAtencionPedidoPhone(normalized.phone);
      const clienteNombre =
        normalized.cliente_nombre ||
        (await resolveAtencionClienteNombre(normalized.phone || telefono)) ||
        '';
      return {
        ...normalized,
        telefono,
        cliente_nombre: clienteNombre || null,
        paciente: clienteNombre || normalized.paciente || 'Cliente mostrador'
      };
    })
  );
}

function atencionItemLabel(item) {
  const producto = String(item?.producto || '').trim();
  if (!producto) return '';
  const presentacion = String(item?.presentacion || '').trim();
  if (presentacion && !producto.toLowerCase().includes(presentacion.toLowerCase())) {
    return `${producto} (${presentacion})`;
  }
  return producto;
}

function atencionItemCantidad(item) {
  const qty = Number(item?.cantidad);
  return Number.isFinite(qty) && qty > 0 ? qty : 1;
}

function atencionOptionalNumber(value) {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function atencionItemTotal(item) {
  const explicitTotal = atencionOptionalNumber(item?.total_linea);
  if (explicitTotal !== null && explicitTotal >= 0) return explicitTotal;
  const unit = atencionOptionalNumber(item?.precio_unitario);
  if (unit === null) return null;
  return unit * atencionItemCantidad(item);
}

function atencionPedidoTotal(items = [], rawTotal = null) {
  const explicitTotal = atencionOptionalNumber(rawTotal);
  if (explicitTotal !== null && explicitTotal >= 0) return explicitTotal;
  const totals = (Array.isArray(items) ? items : [])
    .map((item) => atencionItemTotal(item))
    .filter((value) => Number.isFinite(value));
  if (!totals.length) return null;
  return totals.reduce((acc, value) => acc + value, 0);
}

export function mapAtencionPedidoToFarmaciaCard(pedido) {
  const items = pedido?.items || [];
  const mainItem = items[0] || {};
  const pedidoId = extractAtencionPedidoId(pedido?.atencion_pedido_id || pedido?.id || pedido?.nro_pedido);
  const ingresadoAt = pedido?.farmacia_ingresado_at || pedido?.created_at;
  const ingresadoDate = ingresadoAt ? new Date(ingresadoAt) : new Date();
  const fecha = Number.isNaN(ingresadoDate.getTime())
    ? new Date().toISOString().slice(0, 10)
    : `${ingresadoDate.getFullYear()}-${String(ingresadoDate.getMonth() + 1).padStart(2, '0')}-${String(ingresadoDate.getDate()).padStart(2, '0')}`;

  const telefono = formatAtencionPedidoPhone(pedido?.phone);
  const clienteNombre = String(pedido?.cliente_nombre || pedido?.paciente_nombre || '').trim();

  const farmaciaEstado =
    pedido?.farmacia_estado ||
    (['confirmado', 'borrador'].includes(String(pedido?.status || '').toLowerCase())
      ? 'pedido_generado_farmacia'
      : null);

  const labels = items.map(atencionItemLabel).filter(Boolean);
  const multiple = labels.length > 1;
  const medicamento = multiple
    ? labels.join(' + ')
    : mainItem.producto || 'Producto mostrador';

  const metodoEntrega = String(pedido?.metodo_entrega || '').trim().toLowerCase() || null;
  const direccionEnvio = String(pedido?.direccion_envio || '').trim() || null;
  const esDomicilio = metodoEntrega === 'domicilio';
  const latNum = Number(pedido?.latitud);
  const lngNum = Number(pedido?.longitud);
  const latitud = Number.isFinite(latNum) ? latNum : null;
  const longitud = Number.isFinite(lngNum) ? lngNum : null;
  const direccion = esDomicilio
    ? (direccionEnvio || 'Envío a domicilio')
    : 'Retiro en mostrador';
  const sucursalNombre = String(pedido?.sucursal_nombre || pedido?.sucursal?.nombre || '').trim() || null;
  const sucursalDireccion = String(pedido?.sucursal?.direccion || '').trim() || null;
  const sucursalLocalidad = String(pedido?.sucursal?.localidad || '').trim() || null;
  const sucursalLatitudNum = Number(pedido?.sucursal?.latitud);
  const sucursalLongitudNum = Number(pedido?.sucursal?.longitud);
  const totalPedido = atencionPedidoTotal(items, pedido?.total_pedido);

  return {
    id: Number.isFinite(pedidoId) && pedidoId > 0 ? `atencion-${pedidoId}` : `atencion-${pedido?.id || ''}`,
    atencion_pedido_id: Number.isFinite(pedidoId) && pedidoId > 0 ? pedidoId : pedido.id,
    origen: esDomicilio ? 'bot_domicilio' : 'bot_mostrador',
    phone: pedido?.phone || null,
    telefono,
    cliente_nombre: clienteNombre || null,
    paciente: clienteNombre || 'Cliente mostrador',
    medicamento,
    presentacion: multiple ? '' : mainItem.presentacion || '',
    items: items.map((it) => ({
      idproducto: it.idproducto ?? null,
      producto: it.producto || null,
      presentacion: it.presentacion || '',
      cantidad: it.cantidad ?? 1,
      precio_unitario: it.precio_unitario ?? null,
      total_linea: atencionItemTotal(it),
      stock_q: it.stock_q ?? null
    })),
    items_count: items.length,
    metodo_entrega: metodoEntrega || 'mostrador',
    direccion,
    direccion_envio: direccionEnvio,
    latitud,
    longitud,
    sucursal_nombre: sucursalNombre,
    sucursal: sucursalNombre ? {
      codigo: String(pedido?.sucursal_codigo || pedido?.sucursal?.codigo || '').trim() || null,
      nombre: sucursalNombre,
      direccion: sucursalDireccion,
      localidad: sucursalLocalidad,
      latitud: Number.isFinite(sucursalLatitudNum) ? sucursalLatitudNum : null,
      longitud: Number.isFinite(sucursalLongitudNum) ? sucursalLongitudNum : null
    } : null,
    nro_pedido: formatAtencionPedidoNro(pedido?.nro_pedido, pedidoId || pedido.id),
    estado: farmaciaEstado || 'pedido_generado_farmacia',
    fecha,
    notas: pedido?.notas || null,
    conversation_id: pedido.conversation_id,
    idproducto: mainItem.idproducto || null,
    precio_unitario: mainItem.precio_unitario ?? null,
    total_pedido: totalPedido
  };
}

async function fetchBotPedidosFarmacia({ limit = 150, metodoEntrega, sucursalCodigo } = {}) {
  if (!ATENCION_BOT_BASE_URL) return [];
  try {
    const params = new URLSearchParams();
    if (limit) params.set('limit', String(limit));
    const metodoEntregaKey = String(metodoEntrega || '').trim().toLowerCase();
    if (metodoEntregaKey === 'mostrador' || metodoEntregaKey === 'domicilio') {
      params.set('metodo_entrega', metodoEntregaKey);
    }
    const sucursalCodigoKey = String(sucursalCodigo || '').trim();
    if (sucursalCodigoKey) params.set('sucursal_codigo', sucursalCodigoKey);
    const res = await fetch(`${ATENCION_BOT_BASE_URL}/admin/pedidos-farmacia?${params}`);
    if (!res.ok) return [];
    const data = await res.json().catch(() => ({}));
    return Array.isArray(data?.pedidos) ? data.pedidos : [];
  } catch {
    return [];
  }
}

async function fetchBotSucursalesOperativas() {
  if (!ATENCION_BOT_BASE_URL) return [];
  try {
    const res = await fetch(`${ATENCION_BOT_BASE_URL}/admin/sucursales-operativas`);
    if (!res.ok) return [];
    const data = await res.json().catch(() => ({}));
    return Array.isArray(data?.sucursales) ? data.sucursales : [];
  } catch {
    return [];
  }
}

function mergeAtencionFarmaciaPedidos(lists) {
  const merged = new Map();
  for (const list of lists) {
    for (const row of list || []) {
      const card = ensureAtencionFarmaciaCard(row);
      if (!card?.id) continue;
      merged.set(String(card.id), card);
    }
  }
  return Array.from(merged.values());
}

export async function loadAtencionPedidosForFarmacia({ limit = 150, metodoEntrega, sucursalCodigo } = {}) {
  const [pulseRows, botRows] = await Promise.all([
    fetchAtencionPedidosFarmacia({ limit, metodoEntrega, sucursalCodigo }).catch(() => []),
    fetchBotPedidosFarmacia({ limit, metodoEntrega, sucursalCodigo })
  ]);

  let merged = [];
  if (pulseRows.length || botRows.length) {
    merged = mergeAtencionFarmaciaPedidos([pulseRows, botRows]);
  } else {
    try {
      const all = await fetchAtencionPedidos({ limit });
      merged = mergeAtencionFarmaciaPedidos([
        all.filter(
          (p) =>
            p.farmacia_estado ||
            (['confirmado', 'borrador'].includes(String(p.status || '').toLowerCase()) &&
              (p.items || []).length)
        )
      ]);
    } catch {
      merged = [];
    }
  }

  return enrichAtencionFarmaciaCardsWithNames(merged);
}

export async function loadAtencionSucursalesOperativas() {
  const [pulseRows, botRows] = await Promise.all([
    fetchAtencionSucursalesOperativas().catch(() => []),
    fetchBotSucursalesOperativas()
  ]);
  const byCodigo = new Map();
  for (const row of [...pulseRows, ...botRows]) {
    const codigo = String(row?.codigo || row?.code || '').trim();
    const nombre = String(row?.nombre || row?.name || '').trim();
    if (!codigo || !nombre) continue;
    byCodigo.set(codigo, {
      ...row,
      codigo,
      nombre,
      direccion: row?.direccion || row?.address || '',
      localidad: row?.localidad || row?.city || '',
      cadeteria: Number(row?.cadeteria || 0) === 1 ? 1 : 0
    });
  }
  return Array.from(byCodigo.values()).sort((a, b) =>
    String(a.nombre).localeCompare(String(b.nombre), 'es', { sensitivity: 'base' })
  );
}

export async function confirmarAtencionPedidoFarmacia(pedidoId) {
  const id = extractAtencionPedidoId(pedidoId);
  if (!Number.isFinite(id) || id <= 0) throw new Error('ID de pedido inválido');
  const res = await fetch(`${API_BASE_URL}/api/atencion-bot/pedidos/${id}/confirmar-farmacia`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' }
  });
  return parseJson(res);
}

export async function updateAtencionPedidoFarmaciaEstado(pedidoId, newEstado, origen = 'farmacia') {
  const id = extractAtencionPedidoId(pedidoId);
  if (!Number.isFinite(id) || id <= 0) throw new Error('ID de pedido inválido');
  const res = await fetch(`${API_BASE_URL}/api/atencion-bot/pedidos/${id}/estado-farmacia`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ new_estado: newEstado, origen })
  });
  return parseJson(res);
}

export function isAtencionFarmaciaPedido(pedido) {
  return (
    pedido?.origen === 'bot_mostrador' ||
    Boolean(pedido?.atencion_pedido_id) ||
    String(pedido?.id || '').startsWith('atencion-')
  );
}

export function resolveAtencionPedidoId(pedido) {
  const candidates = [
    pedido?.atencion_pedido_id,
    pedido?.id,
    pedido?.nro_pedido,
    pedido?.pedido_id
  ];
  for (const candidate of candidates) {
    const id = extractAtencionPedidoId(candidate);
    if (Number.isFinite(id) && id > 0) return id;
  }
  return NaN;
}
